import { useEffect, useRef } from 'react'

type TurnstileOptions = {
  sitekey: string
  action: string
  theme: 'light'
  size: 'flexible'
  callback: (token: string) => void
  'error-callback': () => void
  'expired-callback': () => void
  'timeout-callback': () => void
}

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileOptions) => string
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const scriptId = 'cloudflare-turnstile-script'
const scriptUrl = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null
    const script = existingScript ?? document.createElement('script')

    const handleLoad = () => {
      if (window.turnstile) {
        resolve(window.turnstile)
      } else {
        reject(new Error('Turnstile did not initialize.'))
      }
    }

    const handleError = () => reject(new Error('Turnstile could not be loaded.'))

    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })

    if (!existingScript) {
      script.id = scriptId
      script.src = scriptUrl
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }
  })
}

type TurnstileWidgetProps = {
  siteKey: string
  resetKey: number
  onTokenChange: (token: string) => void
  onError: () => void
}

function TurnstileWidget({ siteKey, resetKey, onTokenChange, onError }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const onTokenChangeRef = useRef(onTokenChange)
  const onErrorRef = useRef(onError)

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange
    onErrorRef.current = onError
  }, [onError, onTokenChange])

  useEffect(() => {
    let active = true

    loadTurnstile()
      .then((turnstile) => {
        if (!active || !containerRef.current || widgetIdRef.current) return

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'contact',
          theme: 'light',
          size: 'flexible',
          callback: (token) => onTokenChangeRef.current(token),
          'error-callback': () => {
            onTokenChangeRef.current('')
            onErrorRef.current()
          },
          'expired-callback': () => onTokenChangeRef.current(''),
          'timeout-callback': () => onTokenChangeRef.current(''),
        })
      })
      .catch(() => {
        if (active) onErrorRef.current()
      })

    return () => {
      active = false
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [siteKey])

  useEffect(() => {
    if (resetKey > 0 && window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current)
    }
  }, [resetKey])

  return <div className="turnstile-widget" ref={containerRef} />
}

export default TurnstileWidget
